import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Hero = () => {
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState({
    title: 'AUSTRALIA\'S LARGEST TRANSPORT MARKETPLACE',
    subtitle: 'Machinery • Heavy Haulage • Cars • Trucks • Pallets • General Freight • Shipping Containers • Bulk Transport • Grain • Livestock • Mining • Boats • Trailer • Liquids & more!',
  });
  const [bgImages, setBgImages] = useState([]);
  const [bgIdx, setBgIdx] = useState(0);
  const [announcement, setAnnouncement] = useState(null);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    fetchContent();
    fetchAnnouncement();
  }, []);

  // Auto-flip every 5s when an announcement is live
  useEffect(() => {
    if (!announcement) return;
    const t = setInterval(() => setShowAnnouncement((s) => !s), 5000);
    return () => clearInterval(t);
  }, [announcement]);

  const fetchAnnouncement = async () => {
    try {
      const r = await axios.get(`${API}/announcements/current`);
      if (r.data?.announcement) setAnnouncement(r.data.announcement);
    } catch {
      /* no announcement, hero shows logo only */
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [compRes, galRes] = await Promise.all([
          axios.get(`${API}/competition/photos`).catch(() => ({ data: { photos: [] } })),
          axios.get(`${API}/gallery`).catch(() => ({ data: [] })),
        ]);
        const comp = (compRes.data?.photos || []).map((p) => `${API}/competition/photos/${p.id}/image`);
        const gal = (galRes.data || []).map((g) => g.image_url).filter(Boolean);
        // Interleave for variety
        const combined = [];
        const max = Math.max(comp.length, gal.length);
        for (let i = 0; i < max; i++) {
          if (comp[i]) combined.push(comp[i]);
          if (gal[i]) combined.push(gal[i]);
        }
        if (!cancelled && combined.length > 0) setBgImages(combined);
      } catch { /* ignore, hero looks fine without images */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (bgImages.length < 2) return;
    const t = setInterval(() => setBgIdx((i) => (i + 1) % bgImages.length), 6000);
    return () => clearInterval(t);
  }, [bgImages.length]);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/cms/content/hero`);
      setContent({
        title: response.data.title?.value || content.title,
        subtitle: response.data.subtitle?.value || content.subtitle,
      });
    } catch (error) {
      console.log('Using default hero content');
    }
  };

  const handleGetQuotes = async () => {
    if (!pickup || !dropoff) {
      toast({
        title: "Missing Information",
        description: "Please enter both pickup and dropoff locations",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      // For now, navigate to a quote request page
      // Store the data in localStorage temporarily
      localStorage.setItem('quoteRequest', JSON.stringify({ pickup, dropoff }));
      navigate('/request-quote');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit quote request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative bg-[#1e3a5f] py-4 md:py-12 overflow-hidden">
      {/* Rotating background photos — subtle, sits behind the navy overlay */}
      {bgImages.length > 0 && (
        <div className="absolute inset-0 z-0" aria-hidden data-testid="hero-bg-slider">
          {bgImages.map((src, i) => (
            <img
              key={src + i}
              src={src}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-in-out ${i === bgIdx ? 'opacity-30' : 'opacity-0'}`}
              loading="lazy"
            />
          ))}
          {/* Navy tint on top of the photo so text stays crisp */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#1e3a5f]/70 via-[#1e3a5f]/60 to-[#1e3a5f]/80" />
        </div>
      )}
      {/* Logo (flips to announcement when admin has one scheduled) */}
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="flex justify-center mb-4 md:mb-6">
            <div
              className="logo-flip-container max-w-lg w-full"
              style={{ perspective: '1200px' }}
              data-testid="hero-logo-flipper"
            >
              <div
                className="logo-flip-inner"
                style={{
                  position: 'relative',
                  width: '100%',
                  minHeight: '140px',
                  transition: 'transform 0.9s cubic-bezier(.2,.9,.3,1.2)',
                  transformStyle: 'preserve-3d',
                  transform: showAnnouncement && announcement ? 'rotateY(180deg)' : 'rotateY(0deg)',
                }}
              >
                {/* Front: Logo */}
                <div
                  className="bg-white rounded-xl p-3 md:p-4 shadow-lg"
                  style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
                  data-testid="hero-logo-front"
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_truck-hub-2/artifacts/we3bquku_smallet%20logo%201.jpg"
                    alt="Truckn Send It"
                    className="w-full max-w-xs mx-auto h-auto object-contain"
                  />
                </div>

                {/* Back: Announcement */}
                {announcement && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowAnnouncement(false)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setShowAnnouncement(false)}
                    className="rounded-xl shadow-lg p-5 md:p-6 text-white flex flex-col items-center justify-center text-center cursor-pointer"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                      background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 60%, #991b1b 100%)',
                      border: '2px solid #fecaca',
                    }}
                    data-testid="hero-announcement-back"
                    aria-label="Announcement — click to flip back to logo"
                  >
                    <div className="text-[11px] md:text-xs uppercase tracking-widest font-bold text-red-100 mb-2">
                      📢 Announcement
                    </div>
                    <p className="text-sm md:text-lg font-semibold leading-snug whitespace-pre-wrap break-words" data-testid="hero-announcement-message">
                      {announcement.message}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Key Benefits - Promotional Messages */}
          <div className="mb-6 md:mb-8">
            <p className="text-white/90 text-center text-base md:text-xl mb-4 md:mb-6">
              Our goal at Truckn Send It is to ensure <strong>shippers get a fair price</strong> and <strong>carriers get a fair go!</strong>
            </p>
            <div className="grid grid-cols-3 gap-2 md:gap-4 max-w-4xl mx-auto">
              <div className="bg-white/10 rounded-lg p-2 md:p-4 text-center backdrop-blur-sm border border-white/20">
                <div className="text-xl md:text-2xl mb-1">✓</div>
                <h3 className="text-xs md:text-lg font-bold text-white mb-0.5 md:mb-1 leading-tight">No Hidden Fees</h3>
                <p className="text-white/80 text-[10px] md:text-xs leading-tight">What you see is what you get</p>
              </div>
              <div className="bg-white/10 rounded-lg p-2 md:p-4 text-center backdrop-blur-sm border border-white/20">
                <div className="text-xl md:text-2xl mb-1">🚚</div>
                <h3 className="text-xs md:text-lg font-bold text-white mb-0.5 md:mb-1 leading-tight">Post Loads FREE</h3>
                <p className="text-white/80 text-[10px] md:text-xs leading-tight">No cost to list freight</p>
              </div>
              <div className="bg-white/10 rounded-lg p-2 md:p-4 text-center backdrop-blur-sm border border-white/20">
                <div className="text-xl md:text-2xl mb-1">💰</div>
                <h3 className="text-xs md:text-lg font-bold text-white mb-0.5 md:mb-1 leading-tight">Keep 100%</h3>
                <p className="text-white/80 text-[10px] md:text-xs leading-tight">No load commissions</p>
              </div>
            </div>
          </div>

          <h1 className="text-xl sm:text-2xl md:text-4xl font-bold text-white text-center mb-4 md:mb-6 leading-tight">
            {content.title}
          </h1>

          {/* Call to action for carriers */}
          <div className="text-center">
            <p className="text-white text-sm md:text-base mb-3">
              Apply today to become one of our <strong>trusted and reputable carriers</strong>
            </p>
            <Button 
              onClick={() => navigate('/signup?role=carrier')}
              variant="outline" 
              className="bg-white/10 text-white border-white hover:bg-white hover:text-[#1e3a5f] font-semibold px-5 py-3 text-sm md:text-base"
            >
              Sign Up as Carrier
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;